// A placed pump drawn again at new end sizes (3.15.6). Owner, 2026-10-20: "the pump components have labels for the inlet
// and outlet sizes. When I change them, [the labels] did not change ... the diameter of the stud pattern and the top
// plate should get bigger for larger and smaller diameters. Same as the faceplate."
//
// A placed pump stores no transform. It is the library drawing turned, scaled and moved (the app has no mirror). So the
// pump is drawn in the builder frame at the sizes it shows now (library/pumps.ts pumpRaw), and the turn, scale and move
// that take those parts onto the placed parts are found by a least squares fit (part by part, point by point). The
// pump drawn at the NEW sizes goes through the same turn, scale and move. The group keeps its id, so the pipes on its
// ends stay attached; the ends do not move (the inlet face and the outlet centre are catalogue positions).
//
// A pump that was ungrouped or edited has other parts: it is refused, and only the end size changes (as before 3.15.6).
import type { Vec } from "../core/geometry";
import type { Doc, Entity, Style } from "../doc/entities";
import { makeDeleteCommand, makeUngroupCommand, type Command } from "../doc/commands";
import { newId } from "../doc/document";
import { drawnSizes, pumpRaw, type PumpInfo } from "../library/pumps";
import { pipeMetaOf, rebuildPatches, type FittingPortData } from "./api";

export const CHANGED_PUMP = "This pump was changed after it was placed (ungrouped or edited), so it is not drawn again. Only the end size changed.";

/** p → s·R(θ)·p + t, with θ in degrees clockwise on the screen (the app's turn). */
export interface PumpFit {
  s: number;
  deg: number;
  t: Vec;
}

const rad = (deg: number) => (deg * Math.PI) / 180;

export function applyFit(f: PumpFit, p: Vec): Vec {
  const c = Math.cos(rad(f.deg));
  const s = Math.sin(rad(f.deg));
  return { x: f.s * (p.x * c - p.y * s) + f.t.x, y: f.s * (p.x * s + p.y * c) + f.t.y };
}

const turnDir = (f: PumpFit, d: Vec): Vec => {
  const c = Math.cos(rad(f.deg));
  const s = Math.sin(rad(f.deg));
  return { x: d.x * c - d.y * s, y: d.x * s + d.y * c };
};

/** The points of a part that the fit uses: line points, circle and arc centres, the text anchor. */
function fitPoints(e: Entity): Vec[] {
  if (e.type === "polyline") return e.points;
  if (e.type === "circle" || e.type === "arc") return [e.center];
  if (e.type === "text") return [e.pos];
  return [];
}

const sig = (e: Entity): string => e.type + (e.type === "polyline" ? `:${e.points.length}:${e.closed ? 1 : 0}` : "");

/** The turn, uniform scale and move that take `from` onto `to` best (least squares, no mirror). */
function similarity(from: Vec[], to: Vec[]): PumpFit | null {
  const n = from.length;
  if (n < 2 || to.length !== n) return null;
  const ca = { x: 0, y: 0 };
  const cb = { x: 0, y: 0 };
  for (let i = 0; i < n; i++) {
    ca.x += from[i]!.x / n;
    ca.y += from[i]!.y / n;
    cb.x += to[i]!.x / n;
    cb.y += to[i]!.y / n;
  }
  let sxx = 0;
  let sxy = 0;
  let saa = 0;
  for (let i = 0; i < n; i++) {
    const ax = from[i]!.x - ca.x;
    const ay = from[i]!.y - ca.y;
    const bx = to[i]!.x - cb.x;
    const by = to[i]!.y - cb.y;
    sxx += ax * bx + ay * by;
    sxy += ax * by - ay * bx;
    saa += ax * ax + ay * ay;
  }
  if (saa < 1e-9) return null;
  const theta = Math.atan2(sxy, sxx);
  let s = Math.hypot(sxx, sxy) / saa;
  // the library rounds its points to 0.001 mm: a pump that was never scaled fits at 1 within that
  if (Math.abs(s - 1) < 1e-4) s = 1;
  const f: PumpFit = { s, deg: (theta * 180) / Math.PI, t: { x: 0, y: 0 } };
  const m = applyFit(f, ca);
  f.t = { x: cb.x - m.x, y: cb.y - m.y };
  return f;
}

/** The fit of a placed pump group, or a sentence when its parts are not the parts of its catalogue drawing. */
export function placedPumpFit(doc: Doc, groupId: string): { fit: PumpFit; info: PumpInfo; kids: Entity[] } | string {
  const g = doc.entities[groupId];
  const info = g?.type === "group" ? (g.meta?.pump as PumpInfo | undefined) : undefined;
  if (!g || g.type !== "group" || !info) return "Select a pump.";
  const { dnIn, dnOut } = drawnSizes(info);
  const raw = pumpRaw(info, dnIn, dnOut);
  if (!raw) return "This pump is not in the TraceCAD tables, so it is not drawn again.";
  const kids = g.children.map((c) => doc.entities[c]).filter((e): e is Entity => !!e);
  if (kids.length !== raw.entities.length || kids.some((k, i) => sig(k) !== sig(raw.entities[i]!))) return CHANGED_PUMP;
  const from: Vec[] = [];
  const to: Vec[] = [];
  raw.entities.forEach((e, i) => {
    const a = fitPoints(e);
    const b = fitPoints(kids[i]!);
    for (let j = 0; j < a.length && j < b.length; j++) {
      from.push(a[j]!);
      to.push(b[j]!);
    }
  });
  const fit = similarity(from, to);
  if (!fit) return CHANGED_PUMP;
  // every point must land where it is drawn (an edited part moves points off the fit)
  const tol = 0.5 + 0.002 * fit.s * Math.max(1, ...from.map((p) => Math.hypot(p.x, p.y)));
  for (let i = 0; i < from.length; i++) {
    const p = applyFit(fit, from[i]!);
    if (Math.hypot(p.x - to[i]!.x, p.y - to[i]!.y) > tol) return CHANGED_PUMP;
  }
  return { fit, info, kids };
}

/**
 * One part of the builder drawing placed with the fit, with a new id, on `layerId`, with `style` when given. A text
 * takes `textColor` when given (3.18.0: the colour the person gave the labels of the pump).
 */
function placed(e: Entity, f: PumpFit, layerId: string, style: Style | null, textColor: string | null = null): Entity {
  const id = newId();
  const keep = style ? { style: { ...style } } : {};
  switch (e.type) {
    case "polyline":
      return { ...e, id, layerId, points: e.points.map((p) => applyFit(f, p)), ...keep };
    case "circle":
      return { ...e, id, layerId, center: applyFit(f, e.center), r: e.r * f.s, ...keep };
    case "arc":
      return { ...e, id, layerId, center: applyFit(f, e.center), r: e.r * f.s, startDeg: e.startDeg + f.deg, ...keep };
    case "text":
      return {
        ...e,
        id,
        layerId,
        pos: applyFit(f, e.pos),
        rotationDeg: e.rotationDeg + f.deg,
        textStyle: { ...e.textStyle, size: e.textStyle.size * f.s, ...(textColor ? { color: textColor } : {}) }
      };
    default:
      return { ...e, id, layerId };
  }
}

const styleOf = (e: Entity | undefined): Style | null =>
  e && (e.type === "polyline" || e.type === "circle" || e.type === "arc") ? (e.style as Style) : null;

/**
 * ONE command that draws pump `groupId` again at inlet `dnIn` and outlet `dnOut`: flanges, bolt circles, necks and
 * labels at the new sizes, the ends at the new sizes. The parts keep the colour and line the person gave them (part by
 * part where the part is still there). A sentence instead when the pump cannot be drawn again.
 */
export function redrawPumpCommand(doc: Doc, groupId: string, dnIn: number, dnOut: number): { cmd: Command; ports: FittingPortData[] } | string {
  const placedFit = placedPumpFit(doc, groupId);
  if (typeof placedFit === "string") return placedFit;
  const { fit, info, kids } = placedFit;
  const g = doc.entities[groupId]!;
  if (g.type !== "group") return "Select a pump.";
  const raw = pumpRaw(info, dnIn, dnOut);
  if (!raw) return "This pump is not in the TraceCAD tables, so it is not drawn again.";
  const layerId = kids[0]?.layerId ?? g.layerId;
  // a part keeps its look when the old part at the same place in the list is of the same kind; else the look of the
  // first old part with the same line kind (dashed, dotted, centre) as the new one
  const sameLook = (n: Entity, i: number): Style | null => {
    const o = kids[i];
    if (o && sig(o) === sig(n)) return styleOf(o);
    const ns = styleOf(n);
    if (!ns) return null;
    return kids.map(styleOf).find((s) => s && s.dash === ns.dash) ?? null;
  };
  // the labels keep the colour of the old labels
  const oldText = kids.find((k) => k?.type === "text");
  const textColor = oldText?.type === "text" ? oldText.textStyle.color : null;
  const entities = raw.entities.map((e, i) => placed(e, fit, layerId, sameLook(e, i), textColor));
  const ports: FittingPortData[] = raw.ports.map((pt) => ({
    p: applyFit(fit, pt.p),
    dir: turnDir(fit, pt.dir),
    size: pt.size,
    ...(pt.vertical ? { vertical: true } : {})
  }));
  const pump: PumpInfo = { ...info };
  delete pump.drawnIn;
  delete pump.drawnOut;
  if (dnIn !== info.inletDn) pump.drawnIn = dnIn;
  if (dnOut !== info.outletDn) pump.drawnOut = dnOut;
  const meta = { ...(g.meta ?? {}), pump, ports };
  const groupZ = { layerId: g.layerId, index: (doc.zOrder[g.layerId] ?? []).indexOf(groupId) };
  const redraw: Command[] = [makeUngroupCommand(doc, groupId)];
  if (g.children.length) redraw.push(makeDeleteCommand(doc, [...g.children]));
  redraw.push({ kind: "addEntities", entities, z: entities.map(() => ({ layerId, index: -1 })) });
  redraw.push({
    kind: "group",
    group: { ...structuredClone(g), children: entities.map((e) => e.id), meta },
    z: groupZ,
    children: entities.map((e) => ({ id: e.id, z: { layerId, index: -1 } }))
  });
  return { cmd: { kind: "batch", label: "redraw pump", commands: redraw }, ports };
}

/**
 * Load repair (3.15.6): a pump PLAN placed before 3.15.6 has the inlet end only. It gets the outlet end (vertical, at
 * the centre of the top flange), placed with the fit of its parts. False when the pump cannot be read.
 */
export function addPlanOutletPortInPlace(doc: Doc, groupId: string): boolean {
  const g = doc.entities[groupId];
  const info = g?.type === "group" ? (g.meta?.pump as PumpInfo | undefined) : undefined;
  const ports = g?.meta?.ports as FittingPortData[] | undefined;
  if (!g || g.type !== "group" || !info || info.view !== "plan" || !Array.isArray(ports) || ports.length !== 1) return false;
  const pf = placedPumpFit(doc, groupId);
  if (typeof pf === "string") return false;
  const { dnIn, dnOut } = drawnSizes(info);
  const raw = pumpRaw(info, dnIn, dnOut);
  const out = raw?.ports[1];
  if (!out) return false;
  const port: FittingPortData = { p: applyFit(pf.fit, out.p), dir: turnDir(pf.fit, out.dir), size: dnOut, vertical: true };
  g.meta = { ...(g.meta ?? {}), ports: [...ports, port] };
  return true;
}

/**
 * Load upgrade (3.15.6), in place: every pump plan gets its outlet end, and every pipe end joined to the SIDE of a pump
 * (not the vertical outlet) that is drawn as joined gets a rubber joint. Returns how many pumps and pipe ends changed.
 */
export function upgradePumpsInPlace(doc: Doc): { pumps: number; joints: number } {
  let pumps = 0;
  for (const e of Object.values(doc.entities)) if (e.type === "group" && e.meta?.pump && addPlanOutletPortInPlace(doc, e.id)) pumps++;
  let joints = 0;
  for (const e of Object.values(doc.entities)) {
    const pm = pipeMetaOf(e);
    if (!pm || e.type !== "group") continue;
    const next = { ...pm };
    for (const k of ["a", "b"] as const) {
      const an = pm[k];
      const styleKey = k === "a" ? "endA" : "endB";
      if (an.kind !== "port" || pm[styleKey] !== "none") continue;
      const owner = doc.entities[an.ownerId];
      const port = (owner?.meta?.ports as FittingPortData[] | undefined)?.[an.port];
      if (!owner?.meta?.pump || !port || port.vertical) continue;
      next[styleKey] = "flex";
      joints++;
    }
    if (next.endA !== pm.endA || next.endB !== pm.endB) e.meta = { ...(e.meta ?? {}), pipe: next };
  }
  if (joints > 0) for (const p of rebuildPatches(doc)) Object.assign(doc.entities[p.id]!, structuredClone(p.after));
  return { pumps, joints };
}
